import React, { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import swal from 'sweetalert2';
import moment from 'moment';
import logo from '../assets/images/logo.png';
import {ternUser, ternPassword} from "../env.json";
import { getMeetDetailForDoctor, postForm } from './api/scheduledAPI';
import { uploadPrivateFile } from './api/fileAPI';


const VideoCall = () => {

    const [scheduled,                setScheduled]                = useState(null);
    const [client,                   setClient]                   = useState(null);
    const [doctor,                   setDoctor]                   = useState(null);
    const [callStarted,              setCallStarted]              = useState(false);
    const [validated,                setValidated]                = useState(false);
    const [data,                     setData]                     = useState({});

    const [mediaRecorder,            setMediaRecorder]            = useState(null);

    const [isCameraEnabled,         setIsCameraEnabled]           = useState(true);
    const [isMicEnabled,            setIsMicEnabled]              = useState(true);
    const [screenshots,             setScreenshots]               = useState([]);
    const [video,                   setVideo]                     = useState(null);
    const [location,                setLocation]                  = useState(null);
    const [isClientConnected,       setIsClientConnected]         = useState(false);

    const [socket,                  setSocket]                    = useState(null);
    const [remoteStream,            setRemoteStream]              = useState(null);
    const [isRecording,             setIsRecording]               = useState(false);
    const [isSubmit,                setIsSubmit]               = useState(false);
  
    const localVideoRef                                           = useRef(null);
    const remoteVideoRef                                          = useRef(null);
    const peerConnectionRef                                       = useRef(null);
    const localStreamRef                                          = useRef(null);
    const canvasRef                                               = useRef(null); 

    const screenshotsRef                                          = useRef(screenshots);
    const locationRef                                             = useRef(location);
    const callStartedRef                                          = useRef(callStarted);
    const recordingRef                                            = useRef(isRecording);

    const queryParameters                                         = new URLSearchParams(window.location.search);
    const roomId                                                  = queryParameters.get("room");

    const path =  window.location.hostname === 'localhost'?'http://localhost:8080':(`${window.location.protocol}//console.metromatrixonlineconsultation.com/`);


    useEffect(() => {

        if (window.innerWidth < 1000) {
            swal.fire({ 
                icon: "warning", 
                text: "The video calling feature is currently unavailable on mobile devices. Please use a desktop or laptop for the best experience.", 
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false
            });
            return;
        }

        fetchDetails();

        const newSocket = io(path, { transports: ['websocket'], withCredentials:true });
        
        newSocket.on('connect', ()=> {
            newSocket.emit('doctor-connected', { socketID: newSocket.id, roomID: roomId, reconnecting: callStartedRef.current  })   
        });

        setSocket(newSocket);

        newSocket.on('room-full-doctor', () => {
            setTimeout(() => {
                swal.fire({ 
                    icon: "warning", 
                    text: "Oops! This link is already in use", 
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    allowEscapeKey: false
                });
            }, 1000);
        });

        newSocket.on('client-status', (status) => {
            setIsClientConnected(status);
        });

        newSocket.on('user-connected', () => {
            newSocket.emit('join-room-doctor', roomId);
        });

        newSocket.on('init-web-rtc-doctor', () => {
            setCallStarted(true);
        });

        return () => newSocket.disconnect();
    }, []);

    useEffect(() => {
        screenshotsRef.current = screenshots;
    }, [screenshots]);

    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    useEffect(() => {
        recordingRef.current = isRecording;
        if (!isRecording && mediaRecorder) {
            mediaRecorder.stop(); // Stop the recording
        }
    }, [isRecording]);

    useEffect(() => {
        if (video && isSubmit) {
            postData();
        }
    }, [video, isSubmit]);


    useEffect(() => {
        callStartedRef.current = callStarted;
        try {
            if (callStarted) {
                swal.fire({
                    titleText           : 'Loading',
                    text                : 'Please Wait...',
                    showConfirmButton   : false,
                    allowOutsideClick   : false,
                    didOpen: () => {
                        swal.showLoading();
                    }
                });
                const peerConnection = new RTCPeerConnection({
                    iceServers: [
                        { urls: 'stun:stun.l.google.com:19302' },
                        {
                            urls: 'turn:13.234.196.31:3478',
                            username: ternUser,
                            credential: ternPassword,
                        },
                    ],
                });
    
                peerConnectionRef.current = peerConnection;
    
                peerConnection.onicecandidate = (event) => {
                    if (event.candidate) {
                        socket.emit('signal', {
                            roomId,
                            candidate: event.candidate,
                        });
                    }
                };
    
                peerConnection.ontrack = (event) => {
                    if (event.streams && event.streams[0]) {
                        setRemoteStream(event.streams[0]);
                    } else {
                        console.error("No streams found on track event.");
                    }
                };
             
                socket.on('signal', async (data) => {   
                    if (data.offer) {
                        swal.close();
                        await peerConnection.setRemoteDescription(new RTCSessionDescription(data.offer));
                        const answer = await peerConnection.createAnswer();
                        await peerConnection.setLocalDescription(answer);
                        socket.emit('signal', { roomId, answer });
                    }
        
                    if (data.candidate) {
                        await peerConnection.addIceCandidate(new RTCIceCandidate(data.candidate));
                    }
                });

    
                const initMediaStream = async () => {
                    try {
                        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                        localStreamRef.current = stream;

                        // Add tracks to the peer connection
                        await Promise.all(
                            stream.getTracks().map((track) =>
                                peerConnectionRef.current.addTrack(track, stream)
                            )
                        );

                        if (localVideoRef.current) {
                            localVideoRef.current.srcObject = stream;
                        }

                        socket.emit('web-rtc-doctor', roomId);
        
                    } catch (err) {
                        swal.fire({ 
                            icon: "warning", 
                            text: "Please grant camera and microphone permissions to the browser and reload the page to proceed.", 
                            confirmButtonText: "Reload",
                            allowOutsideClick: false,
                            allowEscapeKey: false
                        }).then((result) => {
                            if (result.isConfirmed) {
                                window.location.reload();
                            }
                        });
                    }
                };
    
                initMediaStream();

                socket.on('location-captured', async (data) => {   
                    setLocation(data.location);
                    swal.fire({
                        icon: "success",
                        title: "Location captured successfully",
                        showConfirmButton: false,
                        timer: 1500
                    });
                });

                socket.on('connection-interrupted', () => {
                    if (callStarted) {
                        // const currentScreenshots = screenshotsRef.current;
                        // const currentLocation = locationRef.current;
                        // if (currentScreenshots.length < 2 || !currentLocation) {
                        //     setTimeout(() => {
                        //         swal.fire({ 
                        //             icon: "warning", 
                        //             text: "Your connection has been interrupted. Please reload the page to restart the call.", 
                        //             confirmButtonText: "Reload",
                        //             allowOutsideClick: false,
                        //             allowEscapeKey: false
                        //         }).then((result) => {
                        //             if (result.isConfirmed) {
                        //                 window.location.reload();
                        //             }
                        //         });
                        //     }, 1000);
                        // } else {
                        //     setTimeout(() => {
                        //         swal.fire({ 
                        //             icon: "warning", 
                        //             text: "Your connection has been interrupted. Please reload the page to restart the call.", 
                        //             showCancelButton: true,
                        //             confirmButtonText: "Reload",
                        //             cancelButtonText: "No, continue form filling",
                        //             allowOutsideClick: false,
                        //             allowEscapeKey: false
                        //         }).then((result) => {
                        //             if (result.isConfirmed) {
                        //                 window.location.reload();
                        //             }
                        //         });
                        //     }, 1000);
                        // }
                    }else{
                        window.location.reload();
                    }
                });  

                window.addEventListener('offline', () => {
                    swal.fire({
                        titleText           : 'Reconnecting',
                        text                : 'Please Wait...',
                        showConfirmButton   : false,
                        allowOutsideClick   : false,
                        didOpen: () => {
                            swal.showLoading();
                        }
                    });
                });
                        
                window.addEventListener('online', () => {
                    swal.close();                                 
                });
            }
        } catch (error) {
            swal.fire({ 
                icon: "warning", 
                text: "Oops! Something went wrong while processing your request. Please try reloading the page and trying again.", 
                confirmButtonText: "Reload",
                allowOutsideClick: false,
                allowEscapeKey: false
            }).then((result) => {
                if (result.isConfirmed) {
                    window.location.reload();
                }
            });
            return; 
        }
        
    }, [callStarted]);

    useEffect(() => {
        if (remoteStream && remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
            startRecording();
        }
        
    }, [remoteStream]);

    const fetchDetails = async() => {
        try {
            swal.fire({
                titleText           : 'Loading',
                text                : 'Please Wait...',
                showConfirmButton   : false,
                allowOutsideClick   : false,
                didOpen: () => {
                    swal.showLoading();
                }
            });
             const { Scheduled, Client, Doctor } = await getMeetDetailForDoctor(roomId);
             setScheduled(Scheduled);
             setClient(Client);
             setDoctor(Doctor);
             swal.close();
        } catch (err) {
            swal.close();
            swal.fire({ 
                icon: "warning", 
                title: "<small>Oops.. this link is not valid!</small>", 
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false
            });
            return;  
        }
    }

    const sendRequest = () => {
        socket.emit('send-request', roomId);
        swal.fire({
            icon: "success",
            title: "Request sent successfully",
            showConfirmButton: false,
            timer: 1500
        });
    };

    const toggleCamera = () => {
        if (localStreamRef.current) {
            const videoTrack = localStreamRef.current.getVideoTracks()[0];
            if (videoTrack) {
                videoTrack.enabled = !videoTrack.enabled;
                setIsCameraEnabled(videoTrack.enabled);
            }
        }
    };

    const toggleMic = () => {
        if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setIsMicEnabled(audioTrack.enabled);
            }
        }
    };

    const disconnectCall = () => {
        swal.fire({
            icon: "question",
            title: "<small>Do you wish to disconnect the call?</small>",
            showDenyButton: true,
            confirmButtonText: "Disconnect and continue",
            denyButtonText: `Disconnect and restart call`,
            confirmButtonColor: '#02b875',
            denyButtonColor: '#4582ec',
            position:'bottom-end'
          }).then((result) => {
            if (result.isConfirmed) {
                remoteStream.getVideoTracks()[0].enabled = false;
                remoteStream.getAudioTracks()[0].enabled = false;
                stopRecording();
                setTimeout(() => {
                    socket.emit('disconnect-call', roomId);
                }, 1000);
            } else if (result.isDenied) {
                socket.emit('restart-call', roomId);
                window.location.reload();
            }
          });
    };


    const takeScreenshot = () => {
        if (remoteVideoRef.current && localVideoRef.current && canvasRef.current) {
            const canvas = canvasRef.current;
            const video1 = remoteVideoRef.current;  
            const video2 = localVideoRef.current;  
            const context = canvas.getContext('2d');
                
            canvas.width = video1.videoWidth;
            canvas.height = video1.videoHeight;
    
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(video1, 0, 0, canvas.width, canvas.height);
            context.drawImage(video2, canvas.width - ((canvas.width * 0.2) + 10) , 10, canvas.width * 0.2, canvas.height * 0.2);

            canvas.toBlob((blob) => {
                if (blob) {
                    setScreenshots((prevScreenshots) => [...prevScreenshots, blob]);
                }
            }, 'image/jpeg', 1);
        }
    };

    const removeScreenshot = (index) => {
        setScreenshots((prevScreenshots) => prevScreenshots.filter((_, i) => i !== index));
    };

    const captureLocation = () => {
        socket.emit('capture-location', roomId);
        swal.fire({
            icon: "success",
            title: "Location request sent",
            showConfirmButton: false,
            timer: 1500
        });

    };

    const startRecording = (restart = false) => {
        try {
          if (remoteStream && localStreamRef.current) {
            // Step 1: Create a canvas
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
      
            // Set canvas dimensions to match the pnlVideoCall container
            const pnlVideoCall = document.querySelector(".pnlVideoCall");
            const { width, height } = pnlVideoCall.getBoundingClientRect();
            canvas.width = width;
            canvas.height = height;

            let drawInterval; // Keep track of the interval for clearing later

            const startDrawingFrames = () => {
                try {
                    // Clear any existing intervals
                    if (drawInterval) clearInterval(drawInterval);

                    const fps = 30; // Set frame rate (30 FPS)
                    const frameDuration = 1000 / fps;

                    drawInterval = setInterval(() => {
                        // Clear the canvas
                        context.clearRect(0, 0, canvas.width, canvas.height);

                        // Draw remote video
                        if (remoteVideoRef.current && remoteVideoRef.current.readyState >= 2) {
                            context.drawImage(
                                remoteVideoRef.current,
                                0,
                                20,
                                canvas.width,
                                canvas.height * 0.9 // Remote video takes 80% of the canvas
                            );
                        }

                        // Draw local video (PiP in top-right corner)
                        if (localVideoRef.current && localVideoRef.current.readyState >= 2) {
                            const pipWidth = canvas.width * 0.2;
                            const pipHeight = canvas.height * 0.2;
                            context.drawImage(
                                localVideoRef.current,
                                canvas.width - pipWidth - 10,
                                30,
                                pipWidth,
                                pipHeight
                            );
                        }

                    }, frameDuration);
                } catch (error) {
                    // swal.fire({
                    //     icon: "warning",
                    //     text: "Recording is not supported by your browser or there was an error. Try updating your browser or using a different one.",
                    //     confirmButtonText: "Restart call",
                    //     allowOutsideClick: false,
                    //     allowEscapeKey: false,
                    // }).then((result) => {
                    //     if (result.isConfirmed) {
                    //         socket.emit('restart-call', roomId);
                    //         window.location.reload();
                    //     }
                    // });

                    swal.fire({
                        icon: "warning 1",
                        text: error,
                        showConfirmButton: false,
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                }
            };

            const stopDrawingFrames = () => {
                if (drawInterval) {
                    clearInterval(drawInterval);
                    drawInterval = null;
                }
            };

            startDrawingFrames();
      
            // Step 3: Capture the canvas as a video stream
            const videoStream = canvas.captureStream(30); // 30 FPS
      
            // Step 4: Combine audio streams with the video stream
            const audioContext = new AudioContext();
            const destination = audioContext.createMediaStreamDestination();

            remoteStream.getAudioTracks().forEach((track) => {
                const remoteAudioSource = audioContext.createMediaStreamSource(new MediaStream([track]));
                remoteAudioSource.connect(destination);
            });

            const localAudioTrack = localStreamRef.current.getAudioTracks()[0];
            if (localAudioTrack) {
                const localAudioSource = audioContext.createMediaStreamSource(new MediaStream([localAudioTrack]));
                localAudioSource.connect(destination);
            }

            const mixedAudioStream = destination.stream;

            const combinedStream = new MediaStream([...videoStream.getTracks(), ...mixedAudioStream.getTracks()]);

            // Step 5: Record the combined stream
            //const recorder = new MediaRecorder(combinedStream , { mimeType: 'video/mp4; codecs=vp9, mp4a.40.2' });
            const recorder = new MediaRecorder(combinedStream , { mimeType: 'video/mp4; codecs="avc1.64001F, mp4a.40.2"' });
            let recordedChunks = [];

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {                
                  recordedChunks.push(event.data);
                }
            };

            recorder.onstop = async () => {

                stopDrawingFrames();
                setMediaRecorder(null);

                if (recordingRef.current) {
                    // unexpected stop
                    swal.fire({
                        icon: "warning",
                        text: "The recording of this video medical session was unexpectedly stopped by your browser. Please restart the video call to ensure uninterrupted recording.",
                        confirmButtonText: "Restart call",
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            socket.emit('restart-call', roomId);
                            window.location.reload();
                        }
                    });

                }else{

                    const blob = new Blob(recordedChunks, { type: "video/mp4" });
                    setVideo(blob);

                    // const videoUrl = URL.createObjectURL(blob);
            
                    // // Download the recorded video
                    // const link = document.createElement("a");
                    // link.href = videoUrl;
                    // link.download = "recorded_video.mp4";
                    // link.click();
            
                    // // Clean up
                    // URL.revokeObjectURL(videoUrl);

                }
            
            };

            recorder.start();
            setMediaRecorder(recorder);
            setIsRecording(true);

          }else{
            if (restart) {
                swal.fire({
                    icon: "warning",
                    text: "Recording is not supported by your browser or there was an error. Try updating your browser or using a different one.",
                    confirmButtonText: "Restart call",
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                }).then((result) => {
                    if (result.isConfirmed) {
                        socket.emit('restart-call', roomId);
                        window.location.reload();
                    }
                });
            } else {
                setTimeout(() => {
                    startRecording(true);
                }, 1000);
            }           
          }
        } catch (error) {
            // swal.fire({
            //     icon: "warning",
            //     text: "Recording is not supported by your browser or there was an error. Try updating your browser or using a different one.",
            //     confirmButtonText: "Restart call",
            //     allowOutsideClick: false,
            //     allowEscapeKey: false,
            // }).then((result) => {
            //     if (result.isConfirmed) {
            //         socket.emit('restart-call', roomId);
            //         window.location.reload();
            //     }
            // });

          swal.fire({
            icon: "warning 2",
            text: error,
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
          });
        }
    };

    const stopRecording = () => {
        setIsRecording(false);
    };

    const handleData = (key, value)=>{
        setData((d)=> { return {...d, [key] : value} });
    };

    const submitForm = (e) => {
        e.preventDefault();

        const form = e.currentTarget;

        if (screenshots.length < 2) {
            swal.fire({ icon: "warning", title: "<small>Please capture atleast 2 screenshots</small>"});
            return; 
        }

        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            swal.fire({ icon: "warning", title: "<small>Please answer all the required questions</small>"});
        }else{

            if (!location) {
                swal.fire({ 
                    icon: "warning", 
                    title: "<small>Location is not captured</small>",
                    confirmButtonText: "Submit anyway",
                    showCancelButton: true
                }).then((result) => {
                    if (result.isConfirmed) {
                        if (recordingRef.current) {
                            stopRecording();
                            setIsSubmit(true);
                        }else{
                            postData();
                        }
                    }
                });
            }else{
                if (recordingRef.current) {
                    stopRecording();
                    setIsSubmit(true);
                }else{
                    postData();
                }
            }
            
        }       

    };

    const postData = async () => {
        try {   
            swal.fire({
                titleText           : 'Loading',
                text                : 'Please Wait...',
                showConfirmButton   : false,
                allowOutsideClick   : false,
                didOpen: () => {
                    swal.showLoading();
                }
            });

            const form = [];
            for (const item in data) {
                form.push({
                    QuestionID  : item,
                    Answer      : item == 4 ? moment(data[item]).format('DD/MM/YYYY') : data[item]
                })
            }

            form.push({
                QuestionID  : 40,
                Answer      : location ? `${location.latitude}, ${location.longitude}` : ''
            })
           
            const media = []
            for await (const item of screenshots) {
                const res = await uploadPrivateFile(item, 'Image');                
                media.push({
                    Type: 'Screenshot',
                    Name: res.FileName
                })
            }

            const file = await uploadPrivateFile(video, 'Video');

            media.push({
                Type: 'Video',
                Name: file.FileName
            })

            await postForm(client.ID, form, media);
            await fetchDetails();
            setCallStarted(false);
            socket.emit('end-call', roomId);
            swal.close();
            
        } catch (err) {
            swal.close();
            swal.fire({ icon: "error", titleText: "Error!", text: err.message });
            return;  
        }
    }
      

    return (
        <>
            {(client && scheduled && scheduled.IsCallDone == 'N' && !callStarted) && (
                <div className='container-fluid'>
                    <div className="row justify-content-center align-items-center text-center" style={{ height: '100vh' }}>
                        <div className="col-12">
                            <div className='login-logo margin-t0'>
                                <img width="234" height="88" src={logo} alt="Metro Matrix Online Consultation"/>
                            </div>
                            <h3>Video Medical With</h3>
                            <div className="d-flex justify-content-center align-items-center mt-5 mb-3">
                                <div className="initial-circle-tpa d-flex justify-content-center align-items-center rounded-circle bg-success text-white" style={{ width: '70px', height: '70px' }}>
                                    {client.Name[0]}
                                </div>
                            </div>
                            <h5>{client.Name}</h5>
                            <span className="fs-16 fw-semibold d-block mb-3" style={{ color: '#767676' }}>
                                <i className="bi bi-clock-fill me-2" style={{ color: '#4582ec' }}></i>
                                {moment(scheduled.Date).format('DD MMM')} {scheduled.From} - {scheduled.To}
                            </span>
                            {isClientConnected ? 
                                (<button onClick={sendRequest} type="button" className="btn btn-primary">Send Video Call Request</button>) : 
                                (<div>
                                    <span>Waiting for client to join the link</span>
                                </div>)
                            }
                        </div>
                    </div>
                </div> 
            )}

            {(scheduled && scheduled.IsCallDone == 'Y') && (
                <div className='container-fluid'>
                    <div className="row justify-content-center align-items-center text-center" style={{ height: '100vh' }}>
                        <div className="col-12">
                            <div className='login-logo margin-t0'>
                                <img width="234" height="88" src={logo} alt="Metro Matrix Online Consultation"/>
                            </div>
                            <h3>The video medical has been successfully submitted</h3>
                            <div className="d-flex justify-content-center align-items-center mt-4 mb-3">
                                <div className="initial-circle-tpa d-flex justify-content-center align-items-center rounded-circle bg-success text-white" style={{ width: '70px', height: '70px' }}>
                                    <i className='bi bi-check-lg'></i>
                                </div>
                            </div>
                            <h4>Thank you!</h4>
                        </div>
                    </div>
                </div>
            )}

            {callStarted && (
                <>
                    <div className='container-fluid' style={{ height: '93vh', overflow: 'hidden' }}>
                        <div className='row' style={{ height: '100%' }}>
                            <div className='col-2 mt-3' style={{ height: '90vh', overflowY: 'scroll', scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
                                <div onClick={captureLocation} className="alert alert-dismissible alert-light padding-0 padding-tb20 btnCapture">
                                    <div className="initial-circle-2x me-2" style={{ backgroundColor: location ? '#02b875' : '#17a2b8' }}><i className={`bi ${location ? 'bi-geo-alt-fill' : 'bi-geo-alt'}`}></i></div>
                                    <strong>Location</strong>
                                </div>
                                <div onClick={takeScreenshot} className="alert alert-dismissible alert-light padding-0 padding-tb20 btnCapture">
                                    <div className="initial-circle-2x me-2" style={{ backgroundColor: screenshots.length < 2 ? '#17a2b8' : '#02b875' }}><i className='bi bi-camera'></i></div><br></br>
                                    <strong>Screenshot</strong>
                                </div>
                                {screenshots.length > 0 && screenshots.map((m, i) => (
                                    <div key={i} className="alert alert-dismissible alert-light padding-0 btnCapture">
                                        <button onClick={() => removeScreenshot(i)} type="button" className="btn-close padding-5 margin-5"></button>
                                        <img src={URL.createObjectURL(m)} alt="Screenshot" style={{ width: '100%' }} />
                                    </div>
                                ))}
                                
                            </div>
                            <div className='col-6 mt-2 pnlVideoCall'>
                                <div className='row'>
                                    <div className='col-12'>
                                        <video className='remoteVideoFrame' ref={remoteVideoRef} autoPlay playsInline />
                                        <video className='localVideoFrame' ref={localVideoRef} autoPlay playsInline muted/>
                                        <div className='controls'>
                                            <button className='btnControl' onClick={toggleCamera}>
                                                <i className={`bi ${isCameraEnabled ? 'bi-camera' : 'bi-camera-video-off'}`}></i>
                                            </button>

                                            <button className='btnControl' onClick={toggleMic}>
                                                <i className={`bi ${isMicEnabled ? 'bi-mic' : 'bi-mic-mute'}`}></i>
                                            </button>

                                            {isRecording && (
                                                <button className='btnControl' onClick={disconnectCall}>
                                                    <i className='bi bi-telephone-x'></i>
                                                </button>
                                            )}

                                            <button type="submit" form='medicalForm' className='btnControl' style={{ backgroundColor: '#02b875'}}>
                                                <i className='bi bi-check-lg'></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>                        
                                
                            </div>

                            <div className='col-4 mt-3'>
                                <div className="alert alert-dismissible alert-light me-2 padding-20" style={{ border: '1px solid #02b875', height: '90vh', overflowY: 'scroll'}}>
                                    <form id='medicalForm' className={`row needs-validation ${validated ? "was-validated" : ""}`} noValidate onSubmit={submitForm}>
                                        <div className='col-md-12 text-center'>
                                            <legend>Dr. {doctor.Name}</legend>
                                            <span className="col-form-label mb-2">Proposal No: {client.ProposalNo} | Branch Code: {client.BranchCode}</span><br></br>
                                            <span className="col-form-label mb-2">{moment().format('DD MMMM hh:mm A')}</span>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <fieldset>
                                                <label className="col-form-label mb-2">Identity Proof</label>
                                                <div className="form-check mb-2">
                                                    <input
                                                        className="form-check-input" 
                                                        type="radio"  
                                                        value="Aadhar Card"
                                                        name="identityProof"
                                                        onChange={({ target }) => handleData('1', target.value)}
                                                        checked={data['1'] === 'Aadhar Card'}
                                                        required
                                                    />
                                                    <label className="form-check-label">
                                                        Aadhar Card
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        value="PAN Card"
                                                        name="identityProof"
                                                        onChange={({ target }) => handleData('1', target.value)}
                                                        checked={data['1'] === 'PAN Card'}
                                                    />
                                                    <label className="form-check-label">
                                                        PAN Card
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        value="Driving Licence"
                                                        name="identityProof"
                                                        onChange={({ target }) => handleData('1', target.value)}
                                                        checked={data['1'] === 'Driving Licence'}
                                                    />
                                                    <label className="form-check-label">
                                                        Driving Licence
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        value="Passport"
                                                        name="identityProof"
                                                        onChange={({ target }) => handleData('1', target.value)}
                                                        checked={data['1'] === 'Passport'}
                                                    />
                                                    <label className="form-check-label">
                                                        Passport
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        value="Employee ID"
                                                        name="identityProof"
                                                        onChange={({ target }) => handleData('1', target.value)}
                                                        checked={data['1'] === 'Passport'}
                                                    />
                                                    <label className="form-check-label">
                                                        Employee ID
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        value="Voter ID"
                                                        name="identityProof"
                                                        onChange={({ target }) => handleData('1', target.value)}
                                                        checked={data['1'] === 'Voter ID'}
                                                    />
                                                    <label className="form-check-label">
                                                        Voter ID
                                                    </label>
                                                </div>
                                            </fieldset>              
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">ID Proof Number (in case of Aadhar only last 4 digits)</label>
                                            <input 
                                                onChange={({ target }) => handleData('2', target.value)}
                                                value={ data['2'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer" 
                                                required
                                            />
                                            <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Full name of the life to be assured</label>
                                            <input
                                                onChange={({ target }) => handleData('3', target.value)}
                                                value={ data['3'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Date of Birth</label>
                                            <input
                                                onChange={({ target }) => handleData('4', target.value)}
                                                value={ data['4'] || ''}
                                                type="date" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Age</label>
                                            <input
                                                onChange={({ target }) => handleData('5', target.value)}
                                                value={ data['5'] || ''}
                                                type="number" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <fieldset>
                                                <label className="col-form-label mb-2">Gender</label>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        name="gander" 
                                                        value="Male"
                                                        onChange={({ target }) => handleData('6', target.value)}
                                                        checked={data['6'] === 'Male'}
                                                        required
                                                    />
                                                    <label className="form-check-label">
                                                        Male
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        name="gander" 
                                                        value="Female"
                                                        onChange={({ target }) => handleData('6', target.value)}
                                                        checked={data['6'] === 'Female'}
                                                    />
                                                    <label className="form-check-label">
                                                        Female
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        name="gander"  
                                                        value="Prefer not to say"
                                                        onChange={({ target }) => handleData('6', target.value)}
                                                        checked={data['6'] === 'Prefer not to say'}
                                                    />
                                                    <label className="form-check-label">
                                                        Prefer not to say
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        name="gander" 
                                                        value="Others"
                                                        onChange={({ target }) => handleData('6', target.value)}
                                                        checked={data['6'] === 'Others'}
                                                    />
                                                    <label className="form-check-label">
                                                        Others
                                                    </label>
                                                </div>
                                            </fieldset>              
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Height (in cms)</label>
                                            <input
                                                onChange={({ target }) => handleData('7', target.value)}
                                                value={ data['7'] || ''}
                                                type="number" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Weight (in kgs)</label>
                                            <input
                                                onChange={({ target }) => handleData('8', target.value)}
                                                value={ data['8'] || ''}
                                                type="number" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Whether receiving or ever received any treatment/medication including alternate medicine like ayurveda, homeopathy etc</label>
                                            <input
                                                onChange={({ target }) => handleData('9', target.value)}
                                                value={ data['9'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Undergone any surgery / hospitalized for any medicalcondition / disability / injury due to accident ?</label>
                                            <input
                                                onChange={({ target }) => handleData('10', target.value)}
                                                value={ data['10'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Whether visited the doctor any time in the last 5 years ? If answer to any of the questions 5(a) to (c) is yes -i. Date of surgery/accident/injury/hospitalisationii. Nature and causeiii.Name of Medicineiv.Degree of impairment if anyv. Whether unconscious due to accident, if yes, give duration</label>
                                            <input
                                                onChange={({ target }) => handleData('11', target.value)}
                                                value={ data['11'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">In the last 5 years, if advised to undergo an X-ray/ CT scan / MRI /ECG / TMT / Blood test / Sputum/Throat swab test or any otherinvestigatory or diagnostic tests ? Please specify date , reason ,advised by whom & findings.</label>
                                            <input
                                                onChange={({ target }) => handleData('12', target.value)}
                                                value={ data['12'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Suffering or ever suffered from Novel Coronavirus (Covid-19) orexperienced any of the symptoms (for more than 5 days) such asany fever, Cough, Shortness of breath, Malaise (flu-like tiredness),Rhinorrhea (mucus discharge from the nose), Sore throat, Gastrointestinalsymptoms such as nausea, vomiting and/or diarrhoea,Chills, Repeated shaking with chills, Muscle pain, Headache, Loss oftaste or smell within last 14 days. If yes provide all investigation and treatment reports</label>
                                            <input
                                                onChange={({ target }) => handleData('13', target.value)}
                                                value={ data['13'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Suffering from Hypertension (high blood pressure) or diabetes or blood sugar levels higher than normal or history of sugar /albuminin urine ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('14', target.value)}
                                                value={ data['14'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Since when, any follow up and date and value of last checked blood pressure and sugar levels ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('15', target.value)}
                                                value={ data['15'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Whether on medication ? please give name of the prescribed medicine and dosage</label>
                                            <input 
                                                onChange={({ target }) => handleData('16', target.value)}
                                                value={ data['16'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Whether developed any complications due to diabetes ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('17', target.value)}
                                                value={ data['17'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Whether suffering from any other endocrine disorders such asthyroid disorder etc.?</label>
                                            <input 
                                                onChange={({ target }) => handleData('18', target.value)}
                                                value={ data['18'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Any weight gain or weight loss in last 12 months (other than by dietcontrol or exercise) ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('19', target.value)}
                                                value={ data['19'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Any history of chest pain, heartattack, palpitations andbreathlessness on exertion or irregular heartbeat ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('20', target.value)}
                                                value={ data['20'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Whether suffering from high cholesterol ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('21', target.value)}
                                                value={ data['21'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Whetheron medication for any heart ailment/ high cholesterol?Please state name of the prescribed medicine and dosage</label>
                                            <input 
                                                onChange={({ target }) => handleData('22', target.value)}
                                                value={ data['22'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Whether undergone Surgery such as CABG, open heart surgeryor PTCA ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('23', target.value)}
                                                value={ data['23'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Suffering or ever suffered from any disease related to kidney suchas kidney failure, kidney or ureteral stones, blood or pus in urine orprostate ?</label>
                                            <input
                                                onChange={({ target }) => handleData('24', target.value)}
                                                value={ data['24'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Suffering or ever suffered from any Liver disorders like cirrhosis,hepatitis, jaundice, or disorder of the Spleen or from any lungrelated or respiratory disorders such as Asthma, bronchitis,wheezing, tuberculosis breathing difficulties etc.?</label>
                                            <input 
                                                onChange={({ target }) => handleData('25', target.value)}
                                                value={ data['25'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Suffering or ever suffered from any Blood disorder likeanaemia, thalassemia or any Circulatory disorder ?</label>
                                            <input
                                                onChange={({ target }) => handleData('26', target.value)}
                                                value={ data['26'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Suffering or ever suffered from any form of cancer, leukaemia,tumor, cyst or growth of any kind or enlarged lymph nodes ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('27', target.value)}
                                                value={ data['27'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Suffering or ever suffered from Epilepsy, nervous disorder,multiple sclerosis, tremors, numbness, paralysis, brain stroke ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('28', target.value)}
                                                value={ data['28'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Suffering or ever suffered from any physical impairment/disability /amputation or any congenital disease/abnormality ordisorder of back, neck, muscle, joints, bones, arthritis or gout ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('29', target.value)}
                                                value={ data['29'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Suffering or ever suffered from Hernia or disorder of theStomach / intestines, colitis, indigestion, Peptic ulcer, piles, or anyother disease of the gall bladder or pancreas ?</label>
                                            <input 
                                                onChange={({ target }) => handleData('30', target.value)}
                                                value={ data['30'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">a. Suffering from Depression/Stress/ Anxiety/ Psychosis or any otherMental / psychiatric disorder? b. Whether on treatment or ever taken any treatment, if yes, pleasegive details of treatment, prescribed medicine and dosages</label>
                                            <input
                                                onChange={({ target }) => handleData('31', target.value)}
                                                value={ data['31'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Is there any abnormality of Eyes (partial/total blindness),Ears(deafness/ discharge from the ears), Nose, Throat orMouth, teeth, swelling of gums / tongue, tobacco stains or signs oforal cancer ?</label>
                                            <input
                                                onChange={({ target }) => handleData('32', target.value)}
                                                value={ data['32'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Whether person being examined and/ or his/her spouse/partnertested positive or is/ are under treatment for HIV/AIDS/Sexually transmitted diseases (e.g. syphilis, gonorrhea, etc.)</label>
                                            <input
                                                onChange={({ target }) => handleData('33', target.value)}
                                                value={ data['33'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Ascertain if any other condition / disease / adverse habit (such assmoking/ tobacco chewing/ consumption ofalcohol/drugs etc) which is relevant in assessent of medical risk ofexaminee. (Type No, If Yes mention details)</label>
                                            <input
                                                onChange={({ target }) => handleData('34', target.value)}
                                                value={ data['34'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">For Female: Whether pregnant? If so duration</label>
                                            <input
                                                onChange={({ target }) => handleData('35', target.value)}
                                                value={ data['35'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">For Female: Suffering from any pregnancy related complications</label>
                                            <input
                                                onChange={({ target }) => handleData('36', target.value)}
                                                value={ data['36'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Whether consulted a gynaecologist or undergone any investigation,treatment for any gynaec ailment such as fibroid, cyst or any diseaseof the breasts, uterus, cervix or ovaries etc. or taken / taking anytreatment for the same</label>
                                            <input
                                                onChange={({ target }) => handleData('37', target.value)}
                                                value={ data['37'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <fieldset>
                                                <label className="col-form-label mb-2">FROM MEDICAL EXAMINER’S OBSERVATION/ASSESSMENT WHETHER LIFE TO BE ASSURED APPEARS MENTALLY ANDPHYSICALLY HEALTHY</label>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        name="examiner" 
                                                        value="Yes"
                                                        onChange={({ target }) => handleData('38', target.value)}
                                                        checked={data['38'] === 'Yes'}
                                                    />
                                                    <label className="form-check-label" >
                                                        Yes
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        name="examiner" 
                                                        value="No"
                                                        onChange={({ target }) => handleData('38', target.value)}
                                                        checked={data['38'] === 'No'}
                                                    />
                                                    <label className="form-check-label">
                                                        No
                                                    </label>
                                                </div>
                                            </fieldset>              
                                        </div>

                                        <div className='col-md-12 mb-4'>
                                            <label className="col-form-label">Place</label>
                                            <input
                                                onChange={({ target }) => handleData('39', target.value)}
                                                value={ data['39'] || ''}
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Your answer"
                                                required
                                            />
                                             <div className="invalid-feedback">This is a required question</div>
                                        </div>

                                    </form>
                                </div>
                                
                            </div>

                        </div>
                    </div>
                    <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
                </>
            )}

            <style jsx="true">
                {`
                    body {
                        overflow: hidden;
                    }
                `}
            </style>
            
        </>
        
    );
};

export default VideoCall;
